import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { FiInfo } from "react-icons/fi";
import InfoTooltip from "../ui/Tooltip";

interface DashboardBarChartProps {
  title: string;
  data: number[];
  categories: string[];
  color?: string;
  barName?: string;
  tooltipText?: string;
}

const DashboardBarChart: React.FC<DashboardBarChartProps> = ({
  title,
  data,
  categories,
  color = "#3b82f6",
  barName = "Valor",
  tooltipText,
}) => {
  const chartData = categories.map((category, index) => ({
    name: category,
    value: data[index],
  }));

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 h-full flex flex-col transition-all duration-300 hover:shadow-lg">
      <div className="mb-6 flex items-center gap-2">
        <h3 className="bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-lg font-bold text-transparent">
          {title}
        </h3>
        {tooltipText && (
          <InfoTooltip content={tooltipText}>
            <FiInfo
              className="cursor-help text-purple-400 hover:text-purple-600 dark:text-purple-300 dark:hover:text-purple-400"
              size={16}
            />
          </InfoTooltip>
        )}
      </div>
      <div className="flex-1 w-full min-h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={1} />
                <stop offset="100%" stopColor={color} stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              opacity={0.15}
              vertical={false}
              stroke="#9ca3af"
            />
            <XAxis
              dataKey="name"
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              dy={10}
            />
            <YAxis
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}`}
            />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)",
                color: "#ffffff",
                backdropFilter: "blur(10px)",
                padding: "12px 16px",
              }}
              cursor={{ fill: "rgba(147, 51, 234, 0.1)" }}
            />
            <Bar
              dataKey="value"
              name={barName}
              fill="url(#colorGradient)"
              radius={[8, 8, 0, 0]}
              barSize={35}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DashboardBarChart;
